/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform, Pressable, TouchableOpacity, View } from 'react-native';
import { pd } from '@airasia-phoenix/react-native/utils';
import { StyleSheet } from 'react-native-unistyles';
import { responsiveTextStyle } from '../../utils/responsiveTextStyle';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import SpinnerLoaderView from '@airasia-phoenix/react-native/components/SpinnerLoaderView';
import { SpinnerLoaderViewStyle } from '@airasia-phoenix/react-native/components/SpinnerLoaderView/types';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import { Purpose } from '../../managers/sso';
import { useAutoRequestOtp } from '../../hooks/useAutoRequestOtp';
import { useIsProfileComplete } from '../../hooks/useIsProfileComplete';
import { useVerifyPhoneOtpFlow } from '../../hooks/otp/useVerifyPhoneOtpFlow';
import { maskMobileNumber } from '../../utils/stringUtils';
import {
  getVerifyPhoneTimerText,
  resolveMobileOtpAnalytics,
} from '../../utils/verifyPhoneOtpUtils';
import OTPView, { type OTPViewRef } from '../OTPView/OTPView';
import { type VerifyPhoneViewProps } from './types';
import Close from '@move-travel/react-native-phoenix-icons/Close';
import { ACTIONS, EVENT_NAMES } from '../../config/analytics';
import {
  createAnalyticsLogger,
  getSsoOtpLabels,
} from '../../utils/loginSignupAnalyticsUtils';

export default function VerifyPhoneView({
  purpose = Purpose.USERNAME_CHANGE,
  phoneNumber,
  dialingCode,
  availableChannels,
  onVerified,
  onUpdateMobileNumber,
  showTitle = false,
  showUpdateMobile = true,
  signupMethod,
  testID,
}: VerifyPhoneViewProps) {
  const { t } = useTranslation('account');
  const otpViewRef = useRef<OTPViewRef>(null);
  const startTimer = useCallback(() => otpViewRef.current?.startTimer(), []);
  const { logEvent } = useLogger();
  const { user } = useIsProfileComplete('VerifyPhoneView');

  const flow = useVerifyPhoneOtpFlow({
    purpose,
    phoneNumber,
    dialingCode,
    availableChannels,
    startTimer,
    profileUpdate: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      gender: user?.gender,
      dateOfBirth: user?.dateOfBirth,
    },
    onVerified,
  });

  const otpAnalytics = useMemo(
    () =>
      resolveMobileOtpAnalytics(purpose, flow.resolvedChannels, signupMethod),
    [flow.resolvedChannels, purpose, signupMethod],
  );
  const otpLabels = useMemo(
    () => getSsoOtpLabels(otpAnalytics.verificationMethod, otpAnalytics.method),
    [otpAnalytics],
  );
  const logMobileOtpAnalytics = useMemo(
    () =>
      createAnalyticsLogger(
        logEvent,
        otpAnalytics.screenName,
        otpAnalytics.category,
        otpAnalytics.enabled,
      ),
    [
      logEvent,
      otpAnalytics.category,
      otpAnalytics.enabled,
      otpAnalytics.screenName,
    ],
  );

  useEffect(() => {
    logMobileOtpAnalytics(ACTIONS.screenView, otpLabels.screen);
  }, [logMobileOtpAnalytics, otpLabels.screen]);

  const handleSubmitOtp = useCallback(
    (otp: string) => {
      logMobileOtpAnalytics(EVENT_NAMES.ssoOtpSubmitted, otpLabels.screen);
      flow.submitOtp(otp);
    },
    [flow, logMobileOtpAnalytics, otpLabels.screen],
  );

  const handleRequestOtp = useCallback(() => {
    logMobileOtpAnalytics(ACTIONS.tap, otpLabels.resendSMS);
    flow.requestOtp();
  }, [flow, logMobileOtpAnalytics, otpLabels.resendSMS]);

  const handleRequestWhatsapp = useCallback(() => {
    logMobileOtpAnalytics(ACTIONS.tap, otpLabels.resendWhatsapp);
    flow.requestOtpViaChannel?.('whatsapp');
  }, [flow, logMobileOtpAnalytics, otpLabels.resendWhatsapp]);

  const handleUpdateMobileNumber = useCallback(() => {
    logMobileOtpAnalytics(ACTIONS.tap, otpLabels.updatePhoneNumber);
    onUpdateMobileNumber?.();
  }, [
    logMobileOtpAnalytics,
    onUpdateMobileNumber,
    otpLabels.updatePhoneNumber,
  ]);

  useAutoRequestOtp({
    behaviour: flow.behaviour,
    requestOtp: flow.requestOtp,
    startTimer,
  });

  const maskedPhoneNumber = flow.displayPhoneNumber
    ? maskMobileNumber(flow.displayPhoneNumber, {
        style: 'verification',
        dialingCode: flow.displayDialingCode,
      })
    : '';
  const subtitle = t('verifyPhonePage.subTitle').replace(
    '%s',
    maskedPhoneNumber,
  );
  const expiredTimerText =
    purpose === Purpose.PHONE_VERIFICATION
      ? t('verifyPhonePage.resendViaSms')
      : t('verifyPhonePage.requestNewOtpText');

  return (
    <View style={styles.pageContainer}>
      {showTitle ? (
        <View style={styles.headerRow}>
          <IconView
            height={24}
            width={24}
            tint={getColor(pd.sys.colour.content_primary)}
          >
            <Close />
          </IconView>
          <TextView
            text={t('verifyPhonePage.title')}
            style={styles.title}
            testID={testID ? `${testID}-title` : 'VerifyPhoneView-title'}
          />
        </View>
      ) : null}
      <View style={styles.container}>
        <OTPView
          ref={otpViewRef}
          title={t('verifyPhonePage.title')}
          subtitle={subtitle}
          subtitleSpannableTextsProps={
            maskedPhoneNumber
              ? [
                  {
                    spannable: maskedPhoneNumber,
                    style: [styles.subtitleEmphasis],
                  },
                ]
              : undefined
          }
          showTitle={Platform.OS !== 'web'}
          errorMessage={flow.errorMessage}
          errorCode={flow.errorCode}
          onOTPSubmit={handleSubmitOtp}
          onRequestNewOTP={handleRequestOtp}
          onErrorClear={flow.clearError}
          testID={testID}
          errorTestID={
            testID
              ? `${testID}-ErrorNotification`
              : 'VerifyPhoneView-ErrorNotification'
          }
          timerHeaderText={t('verifyPhonePage.didntGetOtp')}
          getTimerText={(timer) =>
            getVerifyPhoneTimerText(t, timer, expiredTimerText)
          }
          getTimerSpannableTextsProps={(timer) =>
            timer === 0
              ? [
                  {
                    spannable: expiredTimerText,
                    style: [styles.linkText],
                  },
                ]
              : []
          }
          pinInputFieldTextStyle={{ flex: 1 }}
          deferPinMount
          renderFooter={(timer) => (
            <>
              {purpose === Purpose.PHONE_VERIFICATION && timer === 0 ? (
                <Pressable
                  onPress={handleRequestWhatsapp}
                  testID={
                    testID
                      ? `${testID}-request-whatsapp`
                      : 'request-via-whatsapp'
                  }
                >
                  <TextView
                    text={t('verifyPhonePage.requestViaWhatsapp')}
                    style={styles.linkText}
                  />
                </Pressable>
              ) : null}

              {showUpdateMobile ? (
                <TouchableOpacity
                  style={styles.updateMobileContainer}
                  onPress={handleUpdateMobileNumber}
                  testID={testID ? `${testID}-update-mobile` : undefined}
                >
                  <TextView
                    text={t('verifyPhonePage.updateMobileNumber')}
                    style={styles.linkText}
                  />
                </TouchableOpacity>
              ) : null}
            </>
          )}
        />
        {flow.isLoading && (
          <View style={styles.loaderOverlay}>
            <SpinnerLoaderView spinnerStyle={SpinnerLoaderViewStyle.SPINNER} />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create((pd) => ({
  pageContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: pd.ref.space.spacing40.px,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  updateMobileContainer: {
    alignItems: 'flex-start',
  },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: responsiveTextStyle(
    pd.sys.typescale.heading_3,
    pd.sys.typescale.heading_2,
    pd.sys.colour.content_primary,
  ),
  subtitleEmphasis: responsiveTextStyle(
    pd.sys.typescale.body_text_medium,
    pd.sys.typescale.heading_4,
    pd.sys.colour.content_primary,
  ),
  linkText: responsiveTextStyle(
    pd.sys.typescale.sub_body_text_medium,
    pd.sys.typescale.heading_4,
    pd.sys.colour.interactive_link,
  ),
}));
